import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Banknote, Loader2, CheckCircle2 } from "lucide-react";
import { PAYMENT_METHODS } from "./VendorPaymentsDialog";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Display a raw number string ("2500" / "2500.5") as US currency with
 * comma-grouping ("2,500" / "2,500.5"). Preserves a trailing decimal
 * point so the user can still type past it ("2500." → "2,500.").
 */
function formatAmount(raw) {
  if (raw === "" || raw == null) return "";
  const s = String(raw);
  // Allow only digits + a single dot
  const cleaned = s.replace(/[^0-9.]/g, "");
  const [intPart, ...rest] = cleaned.split(".");
  const decPart = rest.join(""); // collapse any extra dots
  const intFmt = intPart === "" ? "" : Number(intPart).toLocaleString("en-US");
  if (cleaned.includes(".")) return `${intFmt}.${decPart.slice(0, 2)}`;
  return intFmt;
}
function parseAmount(formatted) {
  if (!formatted) return "";
  return String(formatted).replace(/,/g, "").replace(/[^0-9.]/g, "");
}

/**
 * Normalize a raw amount string to exactly 2 decimals (e.g. "2500" →
 * "2500.00", "2500.5" → "2500.50"). Returns "" if blank. Used on blur
 * and after autofill so the field always displays US currency cents.
 */
function padToCents(raw) {
  const cleaned = parseAmount(raw);
  if (cleaned === "" || cleaned === ".") return "";
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
}

export default function RecordRentPaymentDialog({ open, onClose, onRecorded, editing }) {
  // `editing` (optional) — when provided, the dialog opens in EDIT mode and
  // PUTs to /payments/manual/{id} instead of POSTing a fresh row. Pass `null`
  // or omit for the default record-new flow.
  const isEdit = !!editing;
  const [leases, setLeases] = useState([]);
  const [form, setForm] = useState({
    lease_id: "",
    amount: "",
    payment_method: "check",
    check_number: "",
    paid_at: todayIso(),
    reference: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/leases").then((r) => {
      const items = (r.data?.items || r.data || []).filter((l) => l.tenant_id);
      setLeases(items);
    }).catch(() => {});
  }, [open]);

  // When opening in edit mode, seed the form from the editing record. When
  // opening fresh (no editing) we reset to defaults so a previous edit's
  // values don't leak into a new payment.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        lease_id: editing.lease_id || "",
        amount: padToCents(editing.amount ?? "") || String(editing.amount ?? ""),
        payment_method: editing.payment_method || "check",
        check_number: editing.check_number || "",
        // paid_at may be a full timestamp; the date input expects YYYY-MM-DD
        paid_at: (editing.paid_at || "").slice(0, 10) || todayIso(),
        reference: editing.reference || "",
        notes: editing.notes || "",
      });
    } else {
      setForm({
        lease_id: "",
        amount: "",
        payment_method: "check",
        check_number: "",
        paid_at: todayIso(),
        reference: "",
        notes: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  // When the user picks a lease, prefill the amount with the monthly rent
  // (padded to 2 decimals so it renders as US currency immediately).
  const onLeaseChange = (lease_id) => {
    const lease = leases.find((l) => l.id === lease_id);
    setForm((p) => ({
      ...p,
      lease_id,
      amount: lease ? padToCents(lease.monthly_rent || "") : p.amount,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lease_id) return toast.error("Choose a lease");
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      const body = {
        amount: amt,
        payment_method: form.payment_method,
        check_number: form.check_number,
        paid_at: form.paid_at,
        reference: form.reference,
        notes: form.notes,
      };
      if (isEdit) {
        // lease_id is intentionally NOT sent on edit — the backend rejects
        // lease reassignment to avoid stale report joins.
        await api.put(`/payments/manual/${editing.id}`, body);
        toast.success("Payment updated");
      } else {
        await api.post("/payments/manual", { ...body, lease_id: form.lease_id });
        toast.success("Payment recorded");
      }
      // Only reset when creating; edits leave the form alone so reopening
      // the same row shows the just-saved values.
      if (!isEdit) {
        setForm({
          lease_id: "",
          amount: "",
          payment_method: "check",
          check_number: "",
          paid_at: todayIso(),
          reference: "",
          notes: "",
        });
      }
      if (onRecorded) onRecorded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || (isEdit ? "Failed to update payment" : "Failed to record payment"));
    } finally {
      setBusy(false);
    }
  };

  const showCheck = form.payment_method === "check" || form.payment_method === "money_order";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-700" />
            {isEdit ? "Edit payment" : "Record a tenant payment"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Lease / tenant">
            <select
              required
              className="input-clean w-full"
              data-testid="record-payment-lease"
              value={form.lease_id}
              onChange={(e) => onLeaseChange(e.target.value)}
              disabled={isEdit}
              title={isEdit ? "Lease can't be changed on an existing payment — delete and re-record to reassign." : undefined}
            >
              <option value="">— Select a lease —</option>
              {leases.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.property?.name || l.property_name || "Property"} · {l.unit_label || ""} ·{" "}
                  {l.tenant_email || l.tenant_name || "tenant"} · ${l.monthly_rent}/mo
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount paid (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  required
                  type="text"
                  inputMode="decimal"
                  className="input-clean w-full pl-7 font-mono"
                  data-testid="record-payment-amount"
                  value={formatAmount(form.amount)}
                  onChange={(e) => setForm({ ...form, amount: parseAmount(e.target.value) })}
                  onBlur={() => setForm((p) => ({ ...p, amount: padToCents(p.amount) }))}
                  placeholder="0.00"
                />
              </div>
            </Field>
            <Field label="Payment method">
              <select
                className="input-clean w-full"
                data-testid="record-payment-method"
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              >
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {showCheck ? (
              <Field label="Check number">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="record-payment-check-number"
                  value={form.check_number}
                  onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                  placeholder="e.g. 1042"
                />
              </Field>
            ) : (
              <Field label="Reference (optional)">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="record-payment-reference"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Confirmation #"
                />
              </Field>
            )}
            <Field label="Date paid">
              <input
                required
                type="date"
                className="input-clean w-full"
                data-testid="record-payment-date"
                value={form.paid_at}
                onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Notes (optional)">
            <textarea
              rows={2}
              maxLength={500}
              className="input-clean w-full text-sm"
              data-testid="record-payment-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Memo / late fee / partial month, etc."
            />
          </Field>

          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose} disabled={busy} data-testid="record-payment-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="record-payment-submit"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {isEdit ? (busy ? "Saving…" : "Save changes") : "Record payment"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
